import { apiClient } from '@/api/client';
import type { components } from '@/api/schema.gen';

export type UserRead = components['schemas']['UserRead'];
export type UserCreate = components['schemas']['UserCreate'];
export type UserUpdate = components['schemas']['UserUpdate'];
export type UserListResponse = components['schemas']['UserListResponse'];
export type UserUsageResponse = components['schemas']['UserUsageResponse'];
export type PreferencesRead = components['schemas']['PreferencesRead'];
export type PreferencesUpdate = components['schemas']['PreferencesUpdate'];
export type RiskPolicyRead = components['schemas']['RiskPolicyRead'];
export type RiskPolicyUpdate = components['schemas']['RiskPolicyUpdate'];
export type OnboardingStatus = components['schemas']['OnboardingStatus'];
export type UserOnboardingSummary = components['schemas']['UserOnboardingSummary'];
export type BackfillResponse = components['schemas']['BackfillResponse'];

export interface UserListParams {
  limit?: number;
  offset?: number;
}

export async function getUsers(params?: UserListParams): Promise<UserListResponse> {
  const response = await apiClient.get<UserListResponse>('/api/v1/users', { params });
  return response.data;
}

export async function getUser(id: string | number): Promise<UserRead> {
  const response = await apiClient.get<UserRead>(`/api/v1/users/${id}`);
  return response.data;
}

export async function createUser(data: UserCreate): Promise<UserRead> {
  const response = await apiClient.post<UserRead>('/api/v1/users', data);
  return response.data;
}

export async function updateUser(id: string | number, data: UserUpdate): Promise<UserRead> {
  const response = await apiClient.patch<UserRead>(`/api/v1/users/${id}`, data);
  return response.data;
}

export async function deleteUser(id: string | number): Promise<void> {
  await apiClient.delete(`/api/v1/users/${id}`);
}

export async function getUserUsage(id: string | number): Promise<UserUsageResponse> {
  const response = await apiClient.get<UserUsageResponse>(`/api/v1/users/${id}/usage`);
  return response.data;
}

export async function getUserPreferences(id: string | number): Promise<PreferencesRead> {
  const response = await apiClient.get<PreferencesRead>(`/api/v1/users/${id}/preferences`);
  return response.data;
}

export async function updateUserPreferences(
  id: string | number,
  data: PreferencesUpdate,
): Promise<PreferencesRead> {
  const response = await apiClient.patch<PreferencesRead>(`/api/v1/users/${id}/preferences`, data);
  return response.data;
}

export async function getUserRiskPolicy(id: string | number): Promise<RiskPolicyRead> {
  const response = await apiClient.get<RiskPolicyRead>(`/api/v1/users/${id}/risk-policy`);
  return response.data;
}

export async function updateUserRiskPolicy(
  id: string | number,
  data: RiskPolicyUpdate,
): Promise<RiskPolicyRead> {
  const response = await apiClient.patch<RiskPolicyRead>(`/api/v1/users/${id}/risk-policy`, data);
  return response.data;
}

export async function getUserOnboardingStatus(id: string | number): Promise<OnboardingStatus> {
  const response = await apiClient.get<OnboardingStatus>(`/api/v1/users/${id}/onboarding-status`);
  return response.data;
}

export async function getMyOnboardingStatus(): Promise<OnboardingStatus> {
  const response = await apiClient.get<OnboardingStatus>('/api/v1/me/onboarding-status');
  return response.data;
}

export async function getUsersOnboardingSummary(): Promise<UserOnboardingSummary[]> {
  const response = await apiClient.get<UserOnboardingSummary[]>('/api/v1/users/onboarding-summary');
  return response.data;
}

export async function backfillProvisioning(): Promise<BackfillResponse> {
  const response = await apiClient.post<BackfillResponse>('/api/v1/users/backfill-provisioning');
  return response.data;
}
